import SettingSectionTitle from '@/components/settings/SettingSectionTitle.tsx';
import { SettingsTextField } from '@/components/settings/SettingsTextField.tsx';
import { useAuditLog } from '@/contexts/AuditLogContext.tsx';
import { AuthContext } from '@/contexts/AuthContext.tsx';
import { SnackbarContext } from '@/contexts/SnackbarContext.tsx';
import { UserContext } from '@/contexts/UserContext.tsx';
import { createAccountAccessToken } from '@/services/AccountsService.ts';
import Button from '@superfunnel/ui-core/v2/Button';
import { updateProfile } from 'firebase/auth';
import { Lock } from 'iconoir-react';
import { useCallback, useContext, useEffect, useRef, useState } from 'react';

export const ProfileSettings = () => {
  const [edit, setEdit] = useState<boolean>(false);
  const clickTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const { selectedAccount } = useContext(UserContext);
  const auditLog = useAuditLog();
  const { show, hide } = useContext(SnackbarContext);
  const { firebaseUser, isSupportAdmin } = useContext(AuthContext);
  const [name, setName] = useState(firebaseUser?.displayName || '');

  const updateName = useCallback(async () => {
    if (!firebaseUser) return;
    if (firebaseUser.displayName === name) return;

    try {
      await updateProfile(firebaseUser, { displayName: name });
      show('Updated');
    } catch (_) {
      show('Failed to update');
    }
  }, [firebaseUser, name, show]);

  const onSave = () => {
    updateName().then(() => setEdit(false));
  };

  const onDiscard = () => {
    setEdit(false);
    setName(firebaseUser?.displayName || '');
  };

  useEffect(() => {
    setEdit(name !== firebaseUser?.displayName);
  }, [firebaseUser, name]);

  useEffect(() => {
    if (!firebaseUser) return;
    setName(firebaseUser.displayName || '');
  }, [firebaseUser]);

  return (
    <div
      className={
        'relative grid h-full w-full max-w-full grid-rows-[1fr_auto] overflow-hidden'
      }
    >
      <div className={'h-full overflow-y-auto'}>
        <div className={'mx-auto flex max-w-161 flex-col gap-5 px-2.5'}>
          <div className={'mb-2.5 flex items-center gap-3'}>
            <img
              className="bg-base-200 size-16 min-w-10 rounded-full"
              src={firebaseUser?.photoURL ?? ''}
              alt=""
            />
            <div className={'space-y-2'}>
              <p className="text-base-900 font-inter text-sm font-medium">
                Profile Photo
              </p>
              {/* <div className={'flex items-center gap-2'}>*/}
              {/*  <Button type={'button'} variant={'outline'} size={'sm'} className={'rounded-2lg text-base-900 font-inter text-sm -tracking-[0.02em] font-medium gap-2 !px-3'}>*/}
              {/*    <RefreshCw className={'size-4'}/>*/}
              {/*    Update*/}
              {/*  </Button>*/}
              {/*  <Button type={'button'} variant={'outline'} size={'sm'} className={'text-base-900 font-inter text-xs font-medium !p-1.5'}>*/}
              {/*    <Trash className={'size-4.5'}/>*/}
              {/*  </Button>*/}
              {/* </div>*/}
            </div>
          </div>
          <SettingsTextField title="Name" value={name} setValue={setName} />
          <div className={'space-y-2'}>
            <h2 className="text-base-700 font-inter text-sm font-medium">
              Email address
            </h2>
            <dd className="rounded-2lg bg-base-100 items center flex justify-between gap-x-6 p-2 shadow-xs sm:mt-0 sm:flex-auto">
              <p className="">{firebaseUser?.email}</p>
              <Lock className={'text-base-500 size-5'} />
            </dd>
          </div>
          {isSupportAdmin && (
            <>
              <SettingSectionTitle
                title="Admin"
                description="This information will be useful for admins only."
              />

              <dl className="mt-6 divide-y divide-gray-100 border-t border-gray-200 text-sm leading-6 [&>*]:pb-6">
                <div className="pt-6 sm:flex">
                  <dt className="font-medium text-gray-900 sm:w-64 sm:flex-none sm:pr-6">
                    User Id
                  </dt>
                  <dd className="mt-1 flex justify-between gap-x-6 sm:mt-0 sm:flex-auto">
                    <div className="text-gray-900">{firebaseUser?.uid}</div>
                  </dd>
                </div>
                <div className="pt-6 sm:flex">
                  <dt
                    className="font-medium text-gray-900 sm:w-64 sm:flex-none sm:pr-6"
                    onClick={(e) => {
                      const clickCount = e.detail;

                      // Clear any pending timeout
                      if (clickTimeoutRef.current) {
                        clearTimeout(clickTimeoutRef.current);
                        clickTimeoutRef.current = null;
                      }

                      if (clickCount === 3) {
                        // Triple-click: Create Zapier Token (immediate)
                        if (!selectedAccount) return;
                        const snId = show(
                          'Creating Zapier Token...',
                          'loading',
                          {
                            disableAutoClose: true,
                          },
                        );
                        auditLog.log(
                          'create',
                          'Create Zapier Token',
                          'account_access_token',
                          selectedAccount.id,
                          'Zapier Access Token',
                          undefined,
                          undefined,
                        );
                        createAccountAccessToken(selectedAccount.id, 'zapier')
                          .then((token) => {
                            hide(snId.id);
                            navigator.clipboard.writeText(token);
                            show(
                              'Zapier Token Created and Copied to Clipboard',
                              'success',
                            );
                          })
                          .catch((error) => {
                            console.error('Error Creating Zapier Token', error);
                            hide(snId.id);
                            show('Error Creating Zapier Token', 'warn');
                          });
                      } else if (clickCount === 2) {
                        // Double-click: Delay to check for triple-click
                        clickTimeoutRef.current = setTimeout(() => {
                          if (!selectedAccount) return;
                          const snId = show(
                            'Creating Account Token...',
                            'loading',
                            {
                              disableAutoClose: true,
                            },
                          );
                          auditLog.log(
                            'create',
                            'Create Account Token',
                            'account_access_token',
                            selectedAccount.id,
                            'Account Access Token',
                            undefined,
                            undefined,
                          );
                          createAccountAccessToken(selectedAccount.id)
                            .then((token) => {
                              hide(snId.id);
                              navigator.clipboard.writeText(token);
                              show(
                                'Account Token Created and Copied to Clipboard',
                                'success',
                              );
                            })
                            .catch((error) => {
                              console.error(
                                'Error Creating Account Token',
                                error,
                              );
                              hide(snId.id);
                              show('Error Creating Account Token', 'warn');
                            });
                        }, 300);
                      }
                    }}
                  >
                    Account Id
                  </dt>
                  <dd className="mt-1 flex justify-between gap-x-6 sm:mt-0 sm:flex-auto">
                    <div className="text-gray-900">{selectedAccount?.id}</div>
                  </dd>
                </div>
              </dl>
            </>
          )}
          {edit && (
            <div className={'flex w-full justify-end gap-3 bg-white pt-2'}>
              <Button variant={'outline'} onClick={onDiscard}>
                Discard
              </Button>
              <Button onClick={onSave}>Save</Button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
